import {
  Bell,
  Calendar,
  CreditCard,
  ShieldCheck,
} from "@ews-admin/global-design-system";
import React from "react";
import { AppNotification, NotificationType } from "../types/notification.definition";
import { formatMoney, toIntlLocale } from "./format.utils";

type IconComponent = React.ComponentType<{ className?: string }>;

const TYPE_ICONS: Record<NotificationType, IconComponent> = {
  appointment: Calendar,
  payment: CreditCard,
  licence: ShieldCheck,
};

const TYPE_CLASSES: Record<NotificationType, string> = {
  appointment: "text-primary-600 bg-ews-primary-50 border-primary-200",
  payment: "text-success-600 bg-success-50 border-success-200",
  licence: "text-warning-600 bg-warning-50 border-warning-200",
};

const FALLBACK_CLASSES = "text-neutral-600 bg-neutral-50 border-neutral-200";

const toKnownType = (type: string): NotificationType | undefined => {
  const lower = (type ?? "").toLowerCase();
  return lower in TYPE_ICONS ? (lower as NotificationType) : undefined;
};

/** Icon for a notification type; unknown types get a generic bell. */
export const getNotificationTypeIcon = (type: string): IconComponent => {
  const known = toKnownType(type);
  return known ? TYPE_ICONS[known] : Bell;
};

/** Text/background/border classes for a notification type badge. */
export const getNotificationTypeClasses = (type: string): string => {
  const known = toKnownType(type);
  return known ? TYPE_CLASSES[known] : FALLBACK_CLASSES;
};

// ─── MED-1653: notification text in the interface language ───────────────────

/** i18next's `t`, narrowed to what these helpers call. */
type Translate = (key: string, options?: Record<string, unknown>) => string;

// Appointment times are shown in the facility's time zone everywhere in the app.
const FACILITY_TZ = "Africa/Dakar";

interface NotificationMessage {
  messageKey: string;
  params: Record<string, unknown>;
}

/**
 * Reads `message_key` / `message_params` from a notification's metadata JSON.
 * Null for a missing or malformed metadata and for older rows without a key.
 */
export const parseNotificationMessage = (
  metadata: string | null | undefined
): NotificationMessage | null => {
  if (!metadata) return null;
  try {
    const parsed: unknown = JSON.parse(metadata);
    if (!parsed || typeof parsed !== "object") return null;
    const { message_key: messageKey, message_params: params } = parsed as Record<string, unknown>;
    if (typeof messageKey !== "string" || !messageKey) return null;
    return {
      messageKey,
      params: params && typeof params === "object" ? (params as Record<string, unknown>) : {},
    };
  } catch {
    return null;
  }
};

/** "12/10/2026 à 09:30" — the date and time of an appointment, as AppointmentCard shows them. */
const formatAppointmentTime = (iso: string, locale: string, t: Translate): string => {
  const date = new Date(iso);
  if (isNaN(date.getTime())) return iso;
  const day = date.toLocaleDateString(locale, { timeZone: FACILITY_TZ });
  const time = date.toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: FACILITY_TZ,
  });
  return `${day} ${t("appointment.manage.card.at")} ${time}`;
};

/** "2026-11-05" (a calendar day, no time zone) → "05/11/2026" / "11/5/2026". */
const formatCalendarDay = (isoDay: string, locale: string): string => {
  const date = new Date(`${isoDay.slice(0, 10)}T00:00:00Z`);
  if (isNaN(date.getTime())) return isoDay;
  return date.toLocaleDateString(locale, { timeZone: "UTC" });
};

const formatParams = (
  params: Record<string, unknown>,
  language: string,
  t: Translate
): Record<string, unknown> => {
  const locale = toIntlLocale(language);
  const formatted: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined) continue;
    if ((key === "appointment_time" || key === "previous_appointment_time") && typeof value === "string") {
      formatted[key] = formatAppointmentTime(value, locale, t);
    } else if (key === "due_date" && typeof value === "string") {
      formatted[key] = formatCalendarDay(value, locale);
    } else if (key === "amount" && !isNaN(Number(value))) {
      formatted[key] = formatMoney(Number(value), language);
    } else {
      formatted[key] = value;
    }
  }
  // "N day(s) left" strings are i18next plurals driven by count
  if (typeof params.days_left === "number") {
    formatted.count = params.days_left;
  }
  return formatted;
};

/**
 * Title and message of a notification in the interface language: from
 * `notifications.messages.<message_key>.title|message` when the metadata carries a
 * message_key with translations, otherwise the (French) text stored by the API.
 */
export const getNotificationText = (
  notification: Pick<AppNotification, "title" | "message" | "metadata">,
  t: Translate,
  language: string
): { title: string; message: string } => {
  const stored = { title: notification.title ?? "", message: notification.message ?? "" };
  const parsed = parseNotificationMessage(notification.metadata);
  if (!parsed) return stored;
  const base = `notifications.messages.${parsed.messageKey}`;
  const params = formatParams(parsed.params, language, t);
  return {
    title: t(`${base}.title`, { ...params, defaultValue: stored.title }),
    message: t(`${base}.message`, { ...params, defaultValue: stored.message }),
  };
};

/**
 * Relative time of a notification in the bell dropdown ("Just now", "5 minutes ago").
 * createdAt is the API's epoch seconds.
 */
export const formatNotificationTime = (
  createdAt: string,
  t: Translate,
  now: number = Date.now()
): string => {
  const date = new Date(Number(createdAt) * 1000);
  if (isNaN(date.getTime())) return "";
  const minutes = Math.floor((now - date.getTime()) / 60000);
  if (minutes < 1) return t("dashboard.recentActivity.time.justNow");
  if (minutes < 60) return t("dashboard.recentActivity.time.minutesAgo", { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t("dashboard.recentActivity.time.hoursAgo", { count: hours });
  return t("dashboard.recentActivity.time.daysAgo", { count: Math.floor(hours / 24) });
};
